/**
 * Deciding which rows to hold and which to ask for.
 *
 * Pure index arithmetic over a table the client has never seen all of. Nothing
 * here touches a row, a request or React: it turns a scroll position into a
 * window, and a wanted window into the one range that still needs fetching.
 */

/** A half-open range of row indexes, `[start, end)`. */
export interface RowRange {
  start: number
  end: number
}

export const rangeLength = (range: RowRange): number =>
  Math.max(0, range.end - range.start)

export const isEmptyRange = (range: RowRange): boolean => rangeLength(range) === 0

export const rangesEqual = (a: RowRange, b: RowRange): boolean =>
  a.start === b.start && a.end === b.end

export function clampRange(range: RowRange, count: number): RowRange {
  const start = Math.max(0, Math.min(range.start, count))
  const end = Math.max(start, Math.min(range.end, count))
  return { start, end }
}

export function intersectRanges(a: RowRange, b: RowRange): RowRange {
  const start = Math.max(a.start, b.start)
  const end = Math.min(a.end, b.end)
  return { start, end: Math.max(start, end) }
}

export interface WindowPlanInput {
  /** How far down the scroll container is, in pixels. */
  scrollTop: number
  /** How tall the scroll container is, in pixels. */
  viewportHeight: number
  rowHeight: number
  /** Total rows in the view, as the backend last reported it. */
  count: number
  /** Rows kept above and below what is on screen, so small scrolls need no fetch. */
  rowPadding?: number
  /**
   * The granularity a window is rounded to, and the largest single request.
   *
   * Rounding is what stops every pixel of scrolling from producing a new range:
   * a window only moves when it crosses one of these boundaries.
   */
  requestSize?: number
}

export const DEFAULT_ROW_PADDING = 16
export const DEFAULT_REQUEST_SIZE = 40

/**
 * How many request-sized blocks the window may span.
 *
 * Three is one on screen with one either side, which is what makes ordinary
 * scrolling free while keeping the window small enough to hold every row of it
 * densely -- the point of the whole design.
 */
export const MAX_WINDOW_BLOCKS = 3

export interface WindowPlan {
  /** The rows on screen, which is what a virtualizer renders. */
  visible: RowRange
  /** The rows to hold, rounded out to request boundaries. */
  buffer: RowRange
}

/**
 * The window for a scroll position.
 *
 * Anchored on the middle row rather than on the top one, so scrolling in either
 * direction has the same amount of slack. Anchoring on the top would leave a
 * window with padding below and none above, and every upward scroll would fetch.
 */
export function planWindow(input: WindowPlanInput): WindowPlan {
  const {
    scrollTop,
    viewportHeight,
    rowHeight,
    count,
    rowPadding = DEFAULT_ROW_PADDING,
    requestSize = DEFAULT_REQUEST_SIZE,
  } = input

  if (count <= 0 || rowHeight <= 0) {
    return { visible: { start: 0, end: 0 }, buffer: { start: 0, end: 0 } }
  }

  const lastIndex = count - 1
  const middle = scrollTop + viewportHeight / 2
  const middleRowIndex = Math.min(
    Math.max(Math.ceil(middle / rowHeight) - 1, 0),
    lastIndex,
  )

  const visible: RowRange = {
    start: Math.max(middleRowIndex - rowPadding, 0),
    // Inclusive in the original; half-open here, so the end is one past the last
    // visible row -- which is what every range operation below assumes.
    end: Math.min(middleRowIndex + rowPadding, lastIndex) + 1,
  }

  const buffer: RowRange = {
    start: Math.max(
      Math.ceil((visible.start - requestSize) / requestSize) * requestSize,
      0,
    ),
    end: Math.min(
      Math.ceil((visible.end - 1 + requestSize) / requestSize) * requestSize,
      count,
    ),
  }

  return { visible, buffer: clampRange(buffer, count) }
}

/** What has to be fetched, and what the window becomes once it arrives. */
export interface FetchPlan {
  /** The rows to request, or null when the window already holds them all. */
  request: RowRange | null
  /** The window after the response is folded in. */
  buffer: RowRange
  /** Rows to drop from the front, or add to it when negative. */
  prepend: number
  /** Rows to drop from the back, or add to it when negative. */
  append: number
}

/**
 * The one request that turns the window the client holds into the one it wants.
 *
 * At most one, deliberately. A window can only move by scrolling, so the wanted
 * range overlaps the held one on one side or replaces it entirely -- there is
 * never a hole in the middle to fill. That is what lets the buffer stay a dense
 * array rather than a sparse one indexed by absolute row number.
 */
export function planFetch(held: RowRange, wanted: RowRange): FetchPlan {
  const overlap = intersectRanges(held, wanted)

  // Nothing in common: the window jumped, from a scrollbar drag or a filter
  // change, and everything held is now the wrong rows.
  if (isEmptyRange(overlap)) {
    return {
      request: isEmptyRange(wanted) ? null : wanted,
      buffer: wanted,
      prepend: 0,
      append: 0,
    }
  }

  const missingBefore = Math.max(0, overlap.start - wanted.start)
  const missingAfter = Math.max(0, wanted.end - overlap.end)

  if (missingBefore === 0 && missingAfter === 0) {
    // The window already covers what is wanted; only the edges are trimmed.
    return {
      request: null,
      buffer: wanted,
      prepend: wanted.start - held.start,
      append: held.end - wanted.end,
    }
  }

  // Extending in both directions at once would need two requests. It only
  // happens when the window grows -- the viewport got taller, or the padding
  // changed -- so the nearer side is fetched and the next scroll settles the
  // rest, rather than issuing two requests that would race each other.
  if (missingBefore > 0 && missingAfter > 0) {
    const side: RowRange =
      missingBefore >= missingAfter
        ? { start: wanted.start, end: overlap.start }
        : { start: overlap.end, end: wanted.end }
    const buffer: RowRange =
      missingBefore >= missingAfter
        ? { start: wanted.start, end: held.end }
        : { start: held.start, end: wanted.end }
    return {
      request: side,
      buffer,
      prepend: buffer.start - held.start,
      append: held.end - buffer.end,
    }
  }

  if (missingBefore > 0) {
    return {
      request: { start: wanted.start, end: overlap.start },
      buffer: wanted,
      prepend: wanted.start - held.start,
      append: held.end - wanted.end,
    }
  }

  return {
    request: { start: overlap.end, end: wanted.end },
    buffer: wanted,
    prepend: wanted.start - held.start,
    append: held.end - wanted.end,
  }
}

/**
 * Whether a window has grown past what one may hold.
 *
 * A window is bounded so the dense buffer stays small; a table of a million rows
 * still holds at most this many. Exceeding it means rows were inserted into a
 * full window, and the excess is dropped from the far end.
 */
export function maxWindowSize(requestSize = DEFAULT_REQUEST_SIZE): number {
  return requestSize * MAX_WINDOW_BLOCKS
}
